import React from "react"
import { getApiUrl } from "utils/api/apiUrl"
import OpusContext, {
  CreateOpusClient,
  ICommandClient,
  IEmbargoesClient,
  IOpusPayOnAccount,
  IOpusExternalPaymentClient,
  IPolicyClient,
  IQuoteClient,
  IRenewalClient,
  ISetQuoteWithdrawReason,
  IShiftCancellationRequest,
  IUserClient,
  IUserUpdateRequest,
  IBlacklistClient,
  IRatingsClient,
  IApiLogClient,
  IReportsClient,
  IAdditionalProductCancellationRequest
} from "./context"
import Axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios"
import { path, pathOr } from "ramda"
import { ICreateEmbargoForm } from "containers/embargoes/views/NewEmbargo/components/EmbargoDetails/types"
import { LayoutLoading } from "components"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useHistory } from "react-router-dom"
import { message } from "antd"
import UnauthorizedView from "containers/default/views/UnauthorizedView"
import qs from "qs"
import { Nullable } from "platform-client/types"
import { useFirebaseAuthContext } from "contexts/authorization/firebase-auth-contex"
import { decodeJWT } from "utils/pocketknife/decode-jwt"
import { getRememberedBranch, rememberBranch } from "./active-branch"

const opusTokenQueryKey = "opusToken"
const firebaseTokenQueryKey = "firebaseToken"

export interface IOpusProviderOptions {
  children?: React.ReactNode
}

const defaultClientOptions: AxiosRequestConfig = {
  timeout: 30000,
  baseURL: `${getApiUrl(window.location.host)}/opus/v1`,
  headers: {
    "Cache-Control": "no-cache, no-store, must-revalidate",
    Pragma: "no-cache",
    "Content-Type": "application/json",
    Accept: "application/json"
  }
}

export const createOpusClient: CreateOpusClient = (
  token,
  options
): AxiosInstance => {
  return Axios.create({
    ...defaultClientOptions,
    ...options,
    headers: {
      authorization: `Bearer ${token}`
    }
  })
}

const sessionStart = async (
  userToken: string,
  companyID?: string,
  options?: AxiosRequestConfig
) => {
  // the session is always started with the firebase token, so the branch must be sent every time
  // otherwise the session is started in the user's default branch
  const params = companyID
    ? { ...(options?.params || {}), companyID }
    : options?.params

  return createOpusClient(userToken)
    .get(`${getApiUrl(window.location.host)}/opus/session/start`, {
      ...options,
      params
    })
    .then(pathOr("", ["data", "data"]))
}

// The API refuses a session in an office the user isn't allowed in (403), eg they've been removed from it.
// Anything else (a timeout, a 5xx, no network) says nothing about the office
const isOfficeRefused = (error: unknown): boolean => {
  const status = (error as AxiosError)?.response?.status

  return status === 401 || status === 403
}

const getActiveBranchID = (token?: string): string | undefined => {
  if (!token) {
    return undefined
  }

  try {
    const value = decodeJWT(token).activecompanyid
    return typeof value === "string" ? value : undefined
  } catch {
    return undefined
  }
}

const OpusProvider = (opts: IOpusProviderOptions): JSX.Element => {
  const authContext = useFirebaseAuthContext()

  const { children } = opts

  const queryClient = useQueryClient()
  const history = useHistory()
  const firebaseUserID = authContext.user?.uid

  const {
    data: userToken,
    isLoading: isUserLoading,
    isError: isUserError
  } = useQuery(
    // keyed by user so one user's token is never served to another
    [firebaseTokenQueryKey, firebaseUserID],
    async () => {
      const token = await authContext.user?.getIdToken(true)
      return token
    },
    { enabled: !!authContext.user }
  )

  const {
    data: token,
    isLoading: isTokenLoading,
    isError: isTokenError
  } = useQuery<string>(
    [opusTokenQueryKey, userToken],
    async () => {
      if (userToken === undefined) {
        throw new Error("auth token not found.")
      }

      const rememberedBranch = getRememberedBranch(firebaseUserID)
      if (!rememberedBranch) {
        return sessionStart(userToken)
      }

      try {
        return await sessionStart(userToken, rememberedBranch)
      } catch (error) {
        // Any other failure is thrown, so the user isn't quietly put in a different office than the one they chose
        // (react-query retries it, with the remembered office)
        if (!isOfficeRefused(error)) {
          throw error
        }

        // The user can no longer work in the remembered office (eg they've been removed from it), so start in their default office.
        // The remembered office is only cleared once that has worked - otherwise (eg an expired login) it would be lost
        const defaultOfficeToken = await sessionStart(userToken)

        rememberBranch(firebaseUserID, undefined)
        message.warning(
          "You no longer have access to the office you were last working in, so you are now working in your default office",
          8
        )

        return defaultOfficeToken
      }
    },
    {
      enabled: !!userToken
    }
  )

  const fetchToken = useMutation<
    string,
    unknown,
    {
      userToken: string
      options?: AxiosRequestConfig
    }
  >(({ userToken, options }) =>
    sessionStart(userToken, getActiveBranchID(token), options)
  )

  // tokens requested for a subject user are started in the current branch
  const getToken = async (options?: AxiosRequestConfig) => {
    if (userToken === undefined) {
      throw new Error("Auth token not found.")
    }

    return await fetchToken.mutateAsync({ userToken, options })
  }

  const activeBranchID = getActiveBranchID(token)

  const switchBranch = async (companyID: string): Promise<boolean> => {
    if (userToken === undefined || companyID === activeBranchID) {
      return false
    }

    try {
      const newToken = await sessionStart(userToken, companyID)

      rememberBranch(firebaseUserID, companyID)
      queryClient.setQueryData([opusTokenQueryKey, userToken], newToken)

      // everything cached belongs to the previous branch
      queryClient.removeQueries({
        predicate: (query) =>
          query.queryKey[0] !== opusTokenQueryKey &&
          query.queryKey[0] !== firebaseTokenQueryKey
      })

      history.push("/clients")
      return true
    } catch {
      message.error("Unable to switch office, please try again")
      return false
    }
  }

  const opusGet = async (url: string, options?: AxiosRequestConfig) => {
    return createOpusClient(token as string).get(url, options)
  }

  const opusPost = async (
    url: string,
    data?: unknown,
    options?: AxiosRequestConfig
  ) => {
    const opusToken = token || (await getToken())
    return createOpusClient(opusToken as string).post(url, data, options)
  }

  const opusPut = async (
    url: string,
    data?: unknown,
    options?: AxiosRequestConfig
  ) => {
    const opusToken = token || (await getToken())
    return createOpusClient(opusToken as string).put(url, data, options)
  }

  const opusDelete = async (
    url: string,
    data?: unknown,
    options?: AxiosRequestConfig
  ) => {
    const opusToken = token || (await getToken())
    return createOpusClient(opusToken as string).delete(url, options)
  }

  const embargoesClient: IEmbargoesClient = {
    getEmbargoes: async () =>
      opusGet(`embargoes/all`).then(path(["data", "data"])),

    addEmbargo: async (data: ICreateEmbargoForm) =>
      opusPost("embargoes", data).then(path(["data", "data"])),

    getEmbargo: async (id: string) =>
      opusGet(`embargoes/${id}`).then(path(["data", "data"])),

    setEmbargoCancel: async (id: string) =>
      opusPost(`/embargoes/${id}/cancel/now`).then(path(["data", "data"]))
  }

  const renewalClient: IRenewalClient = {
    setActiveRenewalQuote: async (policyID, quoteID) =>
      opusPost(`renewal/${policyID}/setquote?quoteID=${quoteID}`).then(
        path(["data", "data"])
      ),
    duplicateRenewalQuote: async (policyID, quoteID) =>
      opusPost(`renewal/${policyID}/duplicatequote?quoteID=${quoteID}`).then(
        path(["data", "data"])
      ),
    getFailedAutobindRenewals: async () =>
      opusGet(`renewal/failedautobinds`).then(path(["data", "data"])),
    getFailedRenewalInvitations: async () =>
      opusGet(`renewal/failedinvitations`).then(path(["data", "data"])),
    getRenewalsPendingManualReview: async () =>
      opusGet(`renewal/dueformanualreview`).then(path(["data", "data"]))
  }

  const commandClient: ICommandClient = {
    regenerateDocument: async (quoteID, policyID, isPolicyCancellation) =>
      opusPost(`command`, {
        command: `job queue generatedocument ${
          policyID ? `--policyID ${policyID}` : ""
        } ${quoteID ? `--quoteID ${quoteID}` : ""} ${
          isPolicyCancellation ? "--documentType policyCancellation" : ""
        } --generateAction GenerateOnly`,
        commands: []
      }).then(path(["data", "data"]))
  }

  const payOnAccount: IOpusPayOnAccount = {
    bindQuote: async (
      quoteID,
      paymentPlanReferenceID,
      encryptedDiscount,
      paymentReference
    ) => {
      const response = await opusPost(`payonaccount/${quoteID}/bind`, {
        paymentPlanReferenceID,
        encryptedDiscount,
        paymentReference
      })

      return response.data.data
    }
  }

  const externalPaymentClient: IOpusExternalPaymentClient = {
    bindQuote: async (
      quoteID,
      paymentPlanReferenceID,
      encryptedDiscount,
      paymentReference
    ) => {
      const response = await opusPost(`externalpayment/${quoteID}/bind`, {
        paymentPlanReferenceID,
        encryptedDiscount,
        paymentReference
      })

      return response.data.data
    }
  }

  const policyClient: IPolicyClient = {
    getPolicyHistory: async (policyId: string) =>
      opusGet(`/policies/${policyId}`).then(path(["data", "data"])),

    getPolicies: async () =>
      opusGet(`/portal/policies/list`).then(path(["data", "data"])),

    getPolicyEvents: async (policyID: string) =>
      opusGet(`/policies/${policyID}/events`).then(path(["data", "data"])),

    getPolicyBundle: async (policyId: string) =>
      opusGet(`/policies/${policyId}/bundle`).then(path(["data", "data"])),

    getPolicyActions: async (policyId: string) =>
      opusGet(`/policies/${policyId}/actions`).then(path(["data", "data"])),

    getPolicyRenewals: async (policyId: string) =>
      opusGet(`renewal/${policyId}/state`).then(path(["data", "data"])),

    getPolicyRenewalsSettings: async (policyProductID: string) =>
      opusGet(`product/${policyProductID}/renewalsettings`).then(
        path(["data", "data"])
      ),

    getPolicyRenewalQuotes: async (policyId: string) =>
      opusGet(`policies/${policyId}/renewalquotes`, {
        params: {
          completeOnly: false
        }
      }).then(path(["data", "data"])),

    getPolicySearch: async (searchQuery: string) =>
      opusGet(`/search`, {
        params: {
          q: searchQuery,
          entityType: "Policy"
        }
      }).then((response) => response.data.data),

    setPolicyRenewalQuote: async (policyID: string) =>
      opusPost(`/renewal/${policyID}/createquote`, { policyID }).then(
        path(["data", "data"])
      ),

    setPolicyRenewalWithdraw: async (policyID: string) =>
      opusPost(`/renewal/${policyID}/withdraw`, { policyID }).then(
        path(["data", "data"])
      ),
    setPolicyRenewalReinstate: async (policyID: string) =>
      opusPost(`/renewal/${policyID}/reinstate`, { policyID }).then(
        path(["data", "data"])
      ),
    setPolicyRenewals: async (
      values: {
        requiresReview: boolean
        allowAutoBind: boolean
        doNotRenew: boolean
      },
      policyID: string
    ) =>
      opusPost(`/renewal/${policyID}/settings`, values).then(
        path(["data", "data"])
      ),
    getPolicyCancelInfo: async (policyID) =>
      opusGet(`policies/cancel/${policyID}/info`).then((response) => {
        return response.data.data
      }),

    getPolicyCancellationInformation: async (policyID, request) =>
      opusPost(`policies/cancel/${policyID}/information`, request).then(
        (res) => res.data.data
      ),

    setPolicyCancellation: async (subjectuserid, requestBody) => {
      const newToken = await getToken({ params: { subjectuserid } })
      return createOpusClient(newToken as string)
        .post("policies/cancel", requestBody)
        .then((response) => {
          return response.data.data
        })
    },
    reinstatePolicy: async (policyID, reinstatement) =>
      opusPost(`policies/${policyID}/reinstate`, reinstatement).then(
        (response) => response.data.data
      ),
    updateCoverStartDate: async (policyID: string, newCoverStartDate: string) =>
      opusPost(
        `/policies/${policyID}/updatecoverstart?newCoverStart=${newCoverStartDate}`
      ).then((response) => response.data.data),
    getPolicyPaymentPlan: async (policyId: string) =>
      opusGet(`/paymentplan?policyID=${policyId}`).then(path(["data", "data"])),
    manualRefund: async (subjectUserID, requestBody) => {
      const newToken = await getToken({ params: { subjectUserID } })
      return createOpusClient(newToken as string)
        .post("payments/manual/refund", requestBody)
        .then((response) => {
          return response.data.data
        })
    },
    manualPayment: async (subjectUserID, requestBody) => {
      const newToken = await getToken({ params: { subjectUserID } })
      return createOpusClient(newToken as string)
        .post("payments/manual/payment", requestBody)
        .then((response) => {
          return response.data.data
        })
    },
    getPolicyPaymentMethod: async (policyID: string) =>
      opusGet(`policies/${policyID}/paymentmethod`).then(
        (response) => response.data.data
      ),
    getRefundAvailability: async (subjectUserID, policyID) => {
      const newToken = await getToken({ params: { subjectUserID } })
      return createOpusClient(newToken as string)
        .get(`payments/${policyID}/refund-availability`)
        .then((response) => response.data.data)
    },
    payScheduledPayment: async (scheduledPaymentId: string) =>
      opusPost(`/paymentplan/${scheduledPaymentId}/pay`).then(
        (response) => response.data.data
      ),
    getPolicyPayments: async (policyID: string) =>
      opusGet(`policies/${policyID}/payments`).then(
        (response) => response.data.data
      ),
    downloadTransactionReceipt: async (policyID: string, paymentID: string) =>
      opusGet(`policies/${policyID}/payments/${paymentID}/downloadreceipt`, {
        responseType: "arraybuffer"
      }).then((response) => {
        return response
      }),
    downloadDocumentPdf: async (
      policyID: string,
      hidePremiumBreakdown = false
    ) =>
      opusGet(`policies/${policyID}/documentpdf`, {
        params: { hidePremiumBreakdown },
        responseType: "arraybuffer"
      }).then((response) => response),
    emailTransactionReceipt: async (
      policyID: string,
      paymentID: string,
      emailAddress: string
    ) =>
      opusPost(
        `policies/${policyID}/payments/${paymentID}/emailreceipt?emailAddress=${encodeURIComponent(
          emailAddress
        )}`
      ).then((response) => {
        return response.data.data
      }),
    updateScheduledPaymentNextRetryDate: async (
      scheduledPaymentId,
      requestBody
    ) =>
      opusPost(
        `paymentplan/${scheduledPaymentId}/setretrydate`,
        requestBody
      ).then((response) => {
        return response.data.data
      }),
    getTransactionalMessages: async (entityID: string) =>
      opusGet(`messages?entityID=${entityID}`).then(
        (response) => response.data.data
      ),
    resendTransactionalEmail: async (id: string, emailAddress: string) =>
      opusPost(
        `messages/resendemail/${id}?emailAddress=${encodeURIComponent(
          emailAddress
        )}`
      ).then((response) => {
        return response.data.data
      }),
    resendDocument: async (
      documentId: string,
      quoteId: Nullable<string>,
      policyId: Nullable<string>,
      emailAddress: string,
      isCancellation: boolean
    ) =>
      opusPost(`document/${documentId}/resend`, {
        quoteId,
        policyId,
        emailAddress,
        isCancellation
      }).then((response) => {
        return response.data.data
      }),
    shiftCancellation: async (request: IShiftCancellationRequest) =>
      opusPost(`policies/${request.policyID}/shiftcancellation`, request).then(
        (response) => {
          return response.data.data
        }
      ),
    getAdditionalProductCancellationInfo: async (
      policyID: string,
      request: IAdditionalProductCancellationRequest
    ) =>
      opusPost(
        `policies/${policyID}/additionalproduct/cancel/information`,
        request
      ).then((response) => {
        return response.data.data
      }),
    additionalProductCancellation: async (
      policyID: string,
      request: IAdditionalProductCancellationRequest
    ) =>
      opusPost(`policies/${policyID}/additionalproduct/cancel`, request).then(
        (response) => {
          return response.data.data
        }
      )
  }

  const userClient: IUserClient = {
    getUsers: async () =>
      opusGet("opususers/list").then(path(["data", "data"])),

    getUsersGroups: async () =>
      opusGet("opusorgs/groups/list").then(path(["data", "data"])),

    setUserInvite: async (data: unknown) =>
      opusPost(`opususers/invite`, data).then((response) => response),

    addUser: async (data: unknown) =>
      opusPost(`opususers/add`, data).then((response) => response),

    editUser: async (userId: string, data: IUserUpdateRequest) =>
      opusPut(`opususers/${userId}`, data).then(path(["data", "data"])),

    getUser: async (userId: string) =>
      opusGet(`opususers/${userId}`).then(path(["data", "data"])),

    setUserDisable: async (userID: string) =>
      opusPost(`opususers/${userID}/disable`, {}).then(path(["data", "data"])),

    setUserResend: async (userID: string) =>
      opusPost(`opususers/${userID}/sendinvite`, {}).then(
        path(["data", "data"])
      ),

    setUserGroup: async (data: { name: string }) =>
      opusPost(`opusorgs/groups/add`, data).then(path(["data", "data"])),

    createUser: async (userEmail: string) =>
      opusPost(`user/create`, { email: userEmail }).then(
        path(["data", "data"])
      ),

    search: async (query, userRoles) =>
      opusGet(`opususers/search`, { params: { query, userRoles } }).then(
        path(["data", "data"])
      )
  }

  const quoteClient: IQuoteClient = {
    setQuoteReview: async (quoteID: string, reviewValue: boolean) =>
      opusPost(`renewal/quote/${quoteID}/review?result=${reviewValue}`).then(
        path(["data", "data"])
      ),

    getQuotes: async (quoteTypes: string[], status?: string) => {
      return opusGet("portal/quotes/list", {
        params: {
          quoteState: status,
          quoteTypes: quoteTypes
        },
        paramsSerializer: (params) => {
          return qs.stringify(params)
        }
      }).then(path(["data", "data"]))
    },
    getQuoteBundle: async (quoteID: string) =>
      opusGet(`/quotes/${quoteID}/bundle`).then(path(["data", "data"])),

    getQuote: async (quoteID: string) =>
      opusGet(`/quotes/${quoteID}`).then(path(["data", "data"])),

    getQuotePaymentPlans: (quoteID: string) =>
      opusGet(`/quotes/${quoteID}/paymentplans`).then(path(["data", "data"])),

    getQuoteProduct: async (quoteID: string) =>
      opusGet(`/quotes/${quoteID}/product`).then(path(["data", "data"])),

    getQuoteNotes: async (quoteID: string) =>
      opusGet(`/notes/quote/${quoteID}`).then(path(["data", "data"])),

    getQuoteEvents: async (quoteID: string) =>
      opusGet(`/quotes/${quoteID}/events`).then(path(["data", "data"])),

    getQuoteSearch: async (searchQuery: string) =>
      opusGet(`/search`, {
        params: {
          q: searchQuery,
          entityType: "Quote"
        }
      }).then(path(["data", "data"])),

    getQuoteDocuments: async (quoteID: string) =>
      opusGet(`/quotes/${quoteID}/documents`).then(path(["data", "data"])),

    downloadDocumentPdf: async (
      quoteID: string,
      hidePremiumBreakdown = false
    ) =>
      opusGet(`quotes/${quoteID}/documentpdf`, {
        params: { hidePremiumBreakdown },
        responseType: "arraybuffer"
      }).then((response) => response),

    getQuotesWithdrawReasons: async (quoteID: string) =>
      opusGet(`/quotes/${quoteID}/withdrawalreasons`).then(
        path(["data", "data"])
      ),

    getQuoteReview: async (quoteID: string) =>
      opusGet(`/quotes/${quoteID}/statementoffact`).then(
        path(["data", "data"])
      ),

    getQuoteRenewalReview: async (quoteID: string) =>
      opusGet(`/quotes/${quoteID}/renewal`).then(path(["data", "data"])),

    setQuoteWithdraw: async (quoteID: string, data: ISetQuoteWithdrawReason) =>
      opusPost(`/quotes/${quoteID}/withdraw`, data).then(
        path(["data", "data"])
      ),

    setQuoteNote: async (
      quoteID: string,
      data: {
        content: string | undefined
      }
    ) => opusPost(`/notes/quote/${quoteID}`, data).then(path(["data", "data"])),

    setQuoteRefer: async (quoteID: string) =>
      opusPost(`/quotes/${quoteID}/refer`, {}).then(path(["data", "data"])),

    addRemoveEndorsements: async (
      quoteID,
      addedEndorsements,
      comment,
      deletedEndorsements
    ) =>
      opusPost(`/quotes/${quoteID}/edit/endorsements`, {
        addedEndorsements,
        comment,
        deletedEndorsements
      }).then(path(["data", "data"])),

    editEndorsement: async (quoteID, referenceID, title, text) =>
      opusPost(`/quotes/${quoteID}/edit/endorsements/${referenceID}`, {
        title,
        text
      }).then(path(["data", "data"])),

    setQuoteConfirm: async (quoteID: string, comment?: string) =>
      opusPost(`/quotes/${quoteID}/confirm`, { comment }).then(
        path(["data", "data"])
      ),

    setBrokerCommission: async (quoteID: string, commissionPercent: number) =>
      opusPost(`/quotes/${quoteID}/modifybrokercommission`, {
        commissionPercent
      }).then(path(["data", "data"])),

    setQuotePaymentLink: async (quoteID: string) =>
      opusPost(`/quotes/${quoteID}/paymentlink`, {}).then(
        path(["data", "data"])
      ),

    getQuotePaymentLink: async (quoteID: string) =>
      opusGet(`/quotes/${quoteID}/paymentlink`).then(path(["data", "data"])),

    invalidateQuotePaymentLink: async (quoteID: string) =>
      opusPost(`/quotes/${quoteID}/invalidatepaymentlink`).then(
        path(["data", "data"])
      ),

    assignQuoteToUser: async (quoteID: string, userID: string) =>
      opusGet(`/quotes/${quoteID}/assignuser?userID=${userID}`).then(
        path(["data", "data"]),
        (error) => {
          // This error code is returned when the quote is already
          // assigned to the specific user. This feels a little hacky
          // but I've chosen to lets the client return a success in this
          // scenario as while the action has failed, it failed because
          // the desired result is there.
          if (error.response.data.error === 4) {
            return true
          } else {
            return false
          }
        }
      ),
    referQuote: async (quoteID: string, comment?: string) =>
      opusPost(`/quotes/${quoteID}/refer`, { comment }).then(
        path(["data", "data"])
      ),
    anonymiseQuote: async (quoteID: string) =>
      opusPost(`/quotes/${quoteID}/anonymise`).then(path(["data", "data"]))
  }

  const blacklistClient: IBlacklistClient = {
    getBlacklist: async () =>
      opusGet("phonenumberblacklist").then(path(["data", "data"])),
    blacklistPhoneNumber: async (phoneNumber: string) =>
      opusPost(`phonenumberblacklist`, {
        phoneNumber
      }).then(path(["data", "data"])),
    removePhoneNumberFromBlacklist: async (phoneNumber: string) =>
      opusDelete(
        `phonenumberblacklist/${encodeURIComponent(phoneNumber)}`
      ).then(path(["data", "data"])),
    phoneNumberIsBlacklisted: async (phoneNumber: string) =>
      opusGet(`phonenumberblacklist/isBlacklisted`, {
        params: {
          phoneNumber: encodeURIComponent(phoneNumber)
        }
      }).then(path(["data", "data"]))
  }

  const ratingsClient: IRatingsClient = {
    list: async () => opusGet("ratings/list").then(path(["data", "data"])),
    productRateVersions: async () =>
      opusGet("ratings/products").then(path(["data", "data"])),
    uploadRatings: async (data) => {
      const response = await opusPost("ratings/upload", data, {
        headers: {
          "content-type": "multipart/form-data"
        },
        timeout: 180000
      })

      return response.data.data
    },
    updateGoLiveDate: async (rateFileID: string, newGoLiveDate: string) =>
      opusPost(
        `/ratings/${rateFileID}/updategolivedate?newGoLiveDate=${newGoLiveDate}`
      ).then((response) => response.data.data)
  }

  const apiLogClient: IApiLogClient = {
    getAllForEntity: async (entityID: string) =>
      opusGet(`apilog/${entityID}`).then(path(["data", "data"]))
  }

  const reportsClient: IReportsClient = {
    getPendingScheduledPayments: async () =>
      opusGet(`reporting/pendingscheduledpayments`).then(path(["data", "data"]))
  }

  if (isTokenLoading || isUserLoading) {
    return <LayoutLoading withWrapper />
  }

  if (isUserError || isTokenError) {
    return <UnauthorizedView />
  }

  return (
    <OpusContext.Provider
      value={{
        opusToken: token,
        getToken,
        activeBranchID,
        switchBranch,
        userClient,
        quoteClient,
        policyClient,
        renewalClient,
        payOnAccount,
        externalPaymentClient,
        embargoesClient,
        commandClient,
        blacklistClient,
        ratingsClient,
        apiLogClient,
        reportsClient
      }}
    >
      {token ? children : <LayoutLoading withWrapper />}
    </OpusContext.Provider>
  )
}

export default OpusProvider
